import { Trans } from '@lingui/react';
import { Button, WithTooltip } from '@orionprotocol/ui';
import cn from 'classnames';
import { MouseEvent, PropsWithChildren, useCallback, useMemo } from 'react';

import { useActiveWallet, useAppNetwork, useReadOnly, useChangeWalletNetwork } from '../../hooks';
import { useWalletType } from '../../api';

import styles from './NetworkProtectionTooltipWrapper.module.scss';

type NetworkProtectionTooltipWrapperProps = {
  fullWidth?: boolean;
  container?: HTMLElement | null;
  className?: string;
  width?: number;
  widthMobile?: number;
  name?: string;
  placement?: 'top' | 'right' | 'bottom' | 'left';
  overlayClassName?: string;
};
function NetworkProtectionTooltipWrapper({
  children,
  className,
  fullWidth,
  container,
  width,
  widthMobile,
  name,
  placement,
  overlayClassName,
}: PropsWithChildren<NetworkProtectionTooltipWrapperProps>): JSX.Element | null {
  const readOnly = useReadOnly();
  const { label: appNetworkLabel, chainId } = useAppNetwork();
  const active = useActiveWallet();
  const walletType = useWalletType();
  const walletIsMetamask = walletType === 'MetaMask';

  const changeWalletNetwork = useChangeWalletNetwork();

  const solveNetworkIssue = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      event.stopPropagation();
      changeWalletNetwork(chainId).catch(() =>
        console.error('Failed to change network'),
      );
    },
    [chainId, changeWalletNetwork],
  );

  const overlay = useMemo(
    () => (
      <>
        <Trans
          id="this_action_requires_network_in_wallet"
          message="This action requires {appNetworkLabel} network in your wallet"
          values={{ appNetworkLabel }}
        />

        <Button
          type="outlined"
          size="xs"
          className={styles.fixNetworkButton}
          onClick={solveNetworkIssue}
        >
          {walletIsMetamask ? (
            <Trans id="switch_network" message="Switch network" />
          ) : (
            <Trans id="switch_wallet" message="Switch wallet" />
          )}
        </Button>
      </>
    ),
    [appNetworkLabel, solveNetworkIssue, walletIsMetamask],
  );

  return active ? (
    <WithTooltip
      tooltip={overlay}
      container={container || null}
      width={width ?? 200}
      mobileWidth={widthMobile}
      side={placement ?? 'top'}
      active={readOnly}
      tooltipClassName={overlayClassName}
    >
      <div className={cn(className, { [styles.fullWidth]: fullWidth })}>
        {children}
      </div>
    </WithTooltip>
  ) : (
    <div className={cn(className, { [styles.fullWidth]: fullWidth })}>
      {children}
    </div>
  );
}

export default NetworkProtectionTooltipWrapper;
